import { motion } from "framer-motion";
import { Globe2, UserPlus, Monitor, Briefcase, Handshake, ArrowRight } from "lucide-react";

const CITIES = [
  "Lagos", "London", "Paris", "Tokyo", "New York", "Lisbon",
  "Berlin", "São Paulo", "Seoul", "Accra", "Johannesburg", "Kingston",
  "Havana", "Cape Town", "Toronto", "Amsterdam",
];

const PILLARS = [
  {
    icon: UserPlus,
    title: "International Guest Teachers",
    desc: "World-class instructors flown in to teach masterclasses across our studios.",
  },
  {
    icon: Globe2,
    title: "Visiting Artists",
    desc: "Choreographers and movement directors in residency — sharing process, not just steps.",
  },
  {
    icon: Monitor,
    title: "Online Learning",
    desc: "Live and on-demand sessions, accessible from anywhere in the world.",
  },
  {
    icon: Briefcase,
    title: "Bespoke Workshops",
    desc: "Custom training packages on request — schools, companies, crews and festivals.",
  },
  {
    icon: Handshake,
    title: "Creative Exchange",
    desc: "Cultural collaboration programmes connecting UK dancers with global communities.",
  },
];

export const GlobalNetwork = () => {
  return (
    <section
      id="global"
      data-testid="global-section"
      className="relative py-20 sm:py-24 overflow-hidden bg-black"
    >
      {/* Ambient global aura */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full pointer-events-none">
        <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(90,24,154,0.18),transparent_60%)]" />
      </div>

      {/* Faint orbit rings */}
      <motion.div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[1100px] rounded-full border border-white/[0.04] pointer-events-none"
        animate={{ rotate: 360 }}
        transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
      />
      <motion.div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-brand-yellow/[0.05] pointer-events-none"
        animate={{ rotate: -360 }}
        transition={{ duration: 70, repeat: Infinity, ease: "linear" }}
      />

      {/* Grain */}
      <div className="absolute inset-0 noise opacity-60 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <Globe2 size={18} className="text-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">08 · Global Training Network</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide leading-[0.95]">
              One academy.
              <br />
              <span className="text-brand-yellow text-glow-yellow">A world</span> of teachers.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-8">
            <p className="text-zinc-300/90 text-base sm:text-lg leading-relaxed">
              USDD operates as a globally connected network — flying in master
              teachers, hosting international artists in residency, and exchanging
              culture across continents.
            </p>
            <div className="mt-6 flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              <span>UK · Africa · Caribbean · Europe · Americas · Asia</span>
            </div>
          </div>
        </div>

        {/* Cities marquee */}
        <div
          data-testid="cities-marquee"
          className="relative -mx-6 sm:-mx-8 py-6 mb-16 overflow-hidden border-y border-white/10"
          style={{
            maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div className="marquee-track items-center gap-12 sm:gap-16">
            {[...CITIES, ...CITIES].map((city, i) => (
              <div key={`${city}-${i}`} className="flex items-center gap-12 sm:gap-16 shrink-0">
                <span className="font-display text-3xl sm:text-5xl text-white/85 tracking-wide whitespace-nowrap">
                  {city}
                </span>
                <span className="text-brand-yellow text-xl">✦</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pillars grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => {
            const isWide = i >= 3;
            return (
              <motion.div
                key={p.title}
                data-testid={`global-pillar-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group relative p-6 rounded-2xl bg-gradient-to-br from-zinc-950 to-black border border-white/10 hover:border-brand-yellow/40 transition-all hover:-translate-y-1 overflow-hidden ${
                  i === 3 ? "lg:col-span-2" : ""
                } ${i === 4 ? "lg:col-span-1" : ""}`}
              >
                {/* corner glow */}
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-brand-yellow/8 blur-3xl group-hover:bg-brand-yellow/15 transition-colors" />

                <div className="relative">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center group-hover:bg-brand-yellow transition-all">
                      <p.icon size={22} className="text-brand-yellow group-hover:text-black transition-colors" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-600">
                      {String(i + 1).padStart(2, "0")} / {String(PILLARS.length).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl text-white tracking-wide leading-tight">{p.title}</h3>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed max-w-md">{p.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer link to dedicated bookings section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-6 rounded-2xl bg-white/[0.03] border border-white/10"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-1">For schools, festivals & brands</div>
            <div className="font-display text-2xl text-white tracking-wide">Want USDD to come to you?</div>
          </div>
          <a
            href="/schools-organisations"
            data-testid="global-bespoke-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-brand-yellow/50 text-brand-yellow font-bold uppercase text-xs tracking-[0.2em] hover:bg-brand-yellow hover:text-black transition-all"
          >
            See bespoke options <ArrowRight size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
